import { Box, Divider, MenuItem, TextField, Typography } from '@mui/material';
import ColorField from './ColorField';
import { FONT_FAMILIES } from './editorUtils';

// Uncontrolled number field committing its clamped value on blur / Enter.
const CommitNumberField = ({ label, value, min, max, helperText, onCommit, disabled }) => (
  <TextField
    label={label}
    type='number'
    size='small'
    fullWidth
    disabled={disabled}
    defaultValue={value ?? ''}
    helperText={helperText}
    inputProps={{ min, max }}
    onKeyDown={event => {
      if (event.key === 'Enter') event.target.blur();
    }}
    onBlur={event => {
      const parsed = Number(event.target.value);
      if (event.target.value === '' || Number.isNaN(parsed)) {
        event.target.value = String(value ?? '');
        return;
      }
      const clamped = Math.min(max, Math.max(min, parsed));
      event.target.value = String(clamped);
      onCommit(clamped);
    }}
  />
);

// "Nachrichtenstil" tab: global message style (width, colors, fonts) and — for
// marketing mails — the personalization fallbacks incl. the booking link target.
// Transactional templates live inside the branded shell, so they neither set the
// outer background nor carry marketing fallbacks.
const MessageStylePanel = ({
  messageStyle,
  variableFallbacks,
  bookingLink,
  onStyleChange,
  onDocChange,
  disabled,
  showOuterBackground = true,
  showFallbacks = true,
}) => {
  const fontValue = FONT_FAMILIES.some(font => font.value === messageStyle.fontFamily)
    ? messageStyle.fontFamily
    : FONT_FAMILIES[0].value;

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant='subtitle2'>Nachrichtenstil</Typography>
      <CommitNumberField
        label='Breite der E-Mail (px)'
        value={messageStyle.width ?? 600}
        min={320}
        max={900}
        helperText='Standard: 600 px'
        disabled={disabled}
        onCommit={width => onStyleChange({ width })}
      />
      {showOuterBackground && (
        <ColorField
          label='Hintergrundfarbe'
          value={messageStyle.backgroundColor || '#f4f4f4'}
          disabled={disabled}
          onChange={backgroundColor => onStyleChange({ backgroundColor })}
        />
      )}
      <ColorField
        label='Inhalts-Hintergrundfarbe'
        value={messageStyle.contentBackgroundColor || '#ffffff'}
        disabled={disabled}
        onChange={contentBackgroundColor => onStyleChange({ contentBackgroundColor })}
      />
      <TextField
        select
        label='Standard-Schriftart'
        size='small'
        fullWidth
        disabled={disabled}
        value={fontValue}
        onChange={event => onStyleChange({ fontFamily: event.target.value })}
      >
        {FONT_FAMILIES.map(font => (
          <MenuItem key={font.value} value={font.value} sx={{ fontFamily: font.value }}>
            {font.label}
          </MenuItem>
        ))}
      </TextField>
      <CommitNumberField
        label='Standard-Schriftgröße (px)'
        value={messageStyle.fontSize ?? 16}
        min={10}
        max={32}
        disabled={disabled}
        onCommit={fontSize => onStyleChange({ fontSize })}
      />
      <ColorField
        label='Standard-Textfarbe'
        value={messageStyle.textColor || '#333333'}
        disabled={disabled}
        onChange={textColor => onStyleChange({ textColor })}
      />
      {showFallbacks && (
        <>
          <Divider />
          <Typography variant='subtitle2'>Personalisierung</Typography>
          <TextField
            label='Fallback Vorname'
            size='small'
            fullWidth
            disabled={disabled}
            defaultValue={variableFallbacks?.vorname || ''}
            helperText='z. B. "Geburtstagskind" — wird verwendet, wenn kein Vorname vorhanden ist'
            onBlur={event =>
              onDocChange({ variableFallbacks: { ...variableFallbacks, vorname: event.target.value } })
            }
          />
          <TextField
            label='Fallback Standort'
            size='small'
            fullWidth
            disabled={disabled}
            defaultValue={variableFallbacks?.standort || ''}
            helperText='Wird verwendet, wenn kein Standort vorhanden ist'
            onBlur={event =>
              onDocChange({ variableFallbacks: { ...variableFallbacks, standort: event.target.value } })
            }
          />
          <TextField
            label='Buchungslink'
            size='small'
            fullWidth
            disabled={disabled}
            defaultValue={bookingLink || ''}
            placeholder='https://'
            helperText='Ziel der Variable {{buchungslink}}'
            onBlur={event => onDocChange({ bookingLink: event.target.value.trim() })}
          />
        </>
      )}
    </Box>
  );
};

export default MessageStylePanel;
